import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { ApiError, setOnAuthExpired, setTokens } from '../api/client';
import { authApi, canUsePos, type AuthUser } from '../api/auth';

const STORAGE_KEY = 'blossom-pos-session';

interface StoredSession {
  accessToken: string;
  refreshToken: string;
  user: AuthUser;
}

interface AuthState {
  user: AuthUser | null;
  ready: boolean;
  /**
   * Whether this role holds any one of these switches. True until the first
   * answer arrives, so a slow network does not blank the till — the API is
   * what refuses, this only decides what is offered.
   */
  can: (...keys: string[]) => boolean;
  /** Set when the Super Admin has taken this role off the till. */
  lockedOut: string | null;
  signIn: (username: string, password: string) => Promise<void>;
  signOut: () => void;
}

/** How often an open till asks what its role may do. */
const PERMISSIONS_POLL_MS = 60_000;

function accessDeniedMessage(err: unknown): string | null {
  if (!(err instanceof ApiError) || err.status !== 403) return null;
  const payload = err.payload as { code?: string } | null;
  return payload?.code === 'APP_ACCESS_DENIED' ? err.message : null;
}

const AuthContext = createContext<AuthState | null>(null);

function readStored(): StoredSession | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as StoredSession) : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  // `ready` gates the first render so a reload doesn't flash the login screen
  // at a staff member who is already signed in.
  const [ready, setReady] = useState(false);
  const [permissions, setPermissions] = useState<Set<string> | null>(null);
  const [lockedOut, setLockedOut] = useState<string | null>(null);

  const signOut = useCallback(() => {
    window.localStorage.removeItem(STORAGE_KEY);
    setTokens({ accessToken: null, refreshToken: null });
    setUser(null);
    setPermissions(null);
  }, []);

  // Asked at sign-in, then on a timer and whenever the window is looked at
  // again, so a switch flipped on the admin panel reaches an open till within
  // a minute without anybody signing out.
  const userId = user?.id;
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    const load = async () => {
      try {
        const mine = await authApi.myPermissions();
        if (cancelled) return;
        if (mine.appAccess === false) {
          setLockedOut(
            'Your role no longer has access to the POS. Ask the Super Admin.',
          );
          signOut();
          return;
        }
        setPermissions(new Set(mine.permissions));
      } catch (err) {
        const denied = accessDeniedMessage(err);
        if (denied && !cancelled) {
          setLockedOut(denied);
          signOut();
        }
        // Anything else: keep the last good answer through a network blip.
      }
    };
    void load();
    const id = window.setInterval(() => void load(), PERMISSIONS_POLL_MS);
    const onFocus = () => void load();
    window.addEventListener('focus', onFocus);
    return () => {
      cancelled = true;
      window.clearInterval(id);
      window.removeEventListener('focus', onFocus);
    };
  }, [userId, signOut]);

  const can = useCallback(
    (...keys: string[]) => !permissions || keys.some((k) => permissions.has(k)),
    [permissions],
  );

  // Restore the session on boot. The counter PC gets closed and reopened all
  // day; making staff sign in again every time would be its own small tax.
  useEffect(() => {
    const stored = readStored();
    if (stored && canUsePos(stored.user?.role)) {
      setTokens({ accessToken: stored.accessToken, refreshToken: stored.refreshToken });
      setUser(stored.user);
    }
    setReady(true);
  }, []);

  // When the refresh token is spent, the request layer calls this and we drop
  // straight back to the login screen rather than leaving staff poking at
  // a dead till.
  useEffect(() => {
    setOnAuthExpired(() => signOut());
    return () => setOnAuthExpired(null);
  }, [signOut]);

  const signIn = useCallback(async (username: string, password: string) => {
    const result = await authApi.login(username, password);
    if (!canUsePos(result.user?.role)) {
      throw new Error(
        'This account is not allowed to use the billing counter. Sign in with a staff account.',
      );
    }
    const session: StoredSession = {
      accessToken: result.accessToken,
      refreshToken: result.refreshToken,
      user: result.user,
    };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    setTokens({ accessToken: session.accessToken, refreshToken: session.refreshToken });
    setLockedOut(null);
    setUser(session.user);
  }, []);

  const value = useMemo<AuthState>(
    () => ({ user, ready, can, lockedOut, signIn, signOut }),
    [user, ready, can, lockedOut, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
